// PURPOSE: List the incident tickets the status bot filed, as a Home panel beside Builds and To-do.
// CONTRACT: Spec 084; the Home API decides which tickets count and their order.
// DEPENDS: Home data, shared Section frame, pager and badge.
// CEILING: Presentation only; tickets are acknowledged and closed on their own page.
import { useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { Badge } from './components';
import { ListPager, useListPage } from './pager';
import { humanize, relativeTime } from './domain';
import { Section } from './home-panels';
import type { HomeErrorReport } from './types';
const SERVICES:Record<string,string>={workspace:'Workspace','hyc-app':'HYC app','salivan-api':'Salivan API','salivan-web':'Salivan web'};
export function serviceLabel(name:string){return SERVICES[name] || humanize(name);}
// The bot's failing text can be a whole JSON report; show its title (or a short line) and keep the rest folded.
export function errorSummary(failing:string){
 const text=failing.trim();
 if(!text) return 'Health check failed';
 const title=/"title":\s*"([^"]+)/.exec(text)?.[1];
 if(title) return title;
 const line=text.split(/\r?\n/)[0];
 return line.length>90?`${line.slice(0,90)}…`:line;
}
// The whole row opens and closes; links inside it keep their own click.
function ErrorRow({row}:{row:HomeErrorReport}) {
 const [open,setOpen]=useState(false);
 const failing=(row.failing || '').trim();
 const toggle=(event:MouseEvent)=>{
  if((event.target as HTMLElement).closest('a,button,.hm-err-detail') || window.getSelection()?.toString()) return;
  setOpen(!open);
 };
 return <li className={`hm-err-item${open?' open':''}`} onClick={toggle}>
  <div className="hm-build-main"><Link className="hm-title" to={`/records/task/${row.id}`}>{serviceLabel(row.service)}</Link>
   <button type="button" className="hm-err-toggle" aria-expanded={open} onClick={()=>setOpen(!open)}><span>{errorSummary(failing)}</span><ChevronDown size={14} aria-hidden="true"/></button></div>
  <div className="hm-build-side"><Badge tone={row.state==='new'?'warning':'neutral'}>{humanize(row.state)}</Badge><span className="muted" title={row.filed_at}>{row.worker?.name || 'Unassigned'} · filed {relativeTime(row.filed_at)}</span>{row.github_url && <a className="text-button hm-err-link" href={row.github_url} target="_blank" rel="noreferrer">GitHub<ExternalLink size={13} aria-hidden="true"/></a>}</div>
  {open && <div className="hm-err-detail">
   {failing ? <pre>{failing}</pre> : <p className="muted">The health check failed with no response body.</p>}
   <p className="muted">Filed {new Date(row.filed_at).toLocaleString()}{row.build?` · build ${row.build.name}`:''}</p>
   <Link className="text-button" to={`/records/task/${row.id}`}>Open ticket</Link>
  </div>}
 </li>;
}
export function ErrorsPanel({rows,resolved}:{rows:HomeErrorReport[];resolved:number}) {
 const list=useListPage(rows,8);
 return <Section className="panel hm-panel" title="Errors" to="/tickets" link="All tickets">
  {rows.length ? <><ul className="hm-list">{list.visible.map(row=><ErrorRow key={row.id} row={row}/>)}</ul><ListPager list={list} label="errors"/>{resolved>0 && <p className="muted hm-note">{resolved} fixed this week</p>}</> : <p className="muted">No open errors.{resolved>0?` ${resolved} fixed this week.`:''}</p>}
 </Section>;
}
